/* eslint-disable o11y-guard/error-tracking */

import { useCallback, useEffect, useState } from 'react'
import type { TogglesDefinitions } from '../../types'
import {
  getToggles,
  getUserAttributes,
  removeUserAttributes,
  setTogglesAttributes,
  setUser,
} from '../service'
import { TogglesUpdatedEvent } from '@/tools/fwf-toggles/client/service/toggles-updated-event'
import { useErrorHandling } from '@/tools/o11y/client'
import { useUserData } from '@/user/useUserData'

export const useFwfToggles = () => {
  const [toggles, setToggles] = useState<TogglesDefinitions>(() => getToggles())
  const { userData } = useUserData()
  const { withErrorTracking, execWithErrorTracking } = useErrorHandling()

  const handleTogglesUpdated = useCallback(
    (event: Event) => {
      const isTogglesUpdatedEvent = event instanceof TogglesUpdatedEvent
      if (!isTogglesUpdatedEvent) {
        throw new Error('Invalid TogglesUpdatedEvent event!')
      }
      setToggles(event.detail)
    },
    [setToggles]
  )

  useEffect(() => {
    execWithErrorTracking(() => {
      document.addEventListener(TogglesUpdatedEvent.name, handleTogglesUpdated)
      const toggles = getToggles()
      setToggles(toggles)
    })

    return withErrorTracking(() =>
      document.removeEventListener(TogglesUpdatedEvent.name, handleTogglesUpdated)
    )
  }, [execWithErrorTracking, withErrorTracking, handleTogglesUpdated])

  useEffect(() => {
    if (userData) {
      const { id, email } = userData
      setUser({ id: String(id), email })
    } else {
      setUser(null)
    }
  }, [userData])

  return {
    toggles,
    setUser,
    setUserAttributes: setTogglesAttributes,
    getUserAttributes,
    removeUserAttributes,
  }
}
